// Ported from Frontend-web/src/components/Chat/VoiceRecorder.jsx for the
// Super Admin support queue — same control, no app-specific imports.
import React, { useState, useRef } from 'react';
import { Mic, Square, Trash2, Send, Loader2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Named once: this app's lint has no jsx-uses-vars, so a bare `motion.button`
// element reads as an unused import.
const MotionButton = motion.button;
const MotionDiv = motion.div;

const VoiceRecorder = ({ onRecordingComplete, disabled, isUploading }) => {
    const { t } = useTranslation();
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                setAudioBlob(blob);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (err) {
            console.error('Error accessing microphone:', err);
            alert(t('voiceRecorder.microphoneAccessError'));
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
        }
    };

    const cancelRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
            setAudioBlob(null);
            chunksRef.current = [];
        } else {
            setAudioBlob(null);
            chunksRef.current = [];
        }
    };

    const sendRecording = () => {
        if (audioBlob) {
            const file = new File([audioBlob], `voice_note_${Date.now()}.webm`, { type: 'audio/webm' });
            onRecordingComplete(file);
            setAudioBlob(null);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="flex items-center gap-2" data-testid="voice-recorder-container">
            <AnimatePresence mode="wait">
                {!isRecording && !audioBlob ? (
                    <MotionButton
                        key="mic"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        type="button"
                        onClick={startRecording}
                        disabled={disabled || isUploading}
                        className={`h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${disabled || isUploading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-[#4ABDBD]/10 text-[#4ABDBD] hover:bg-[#4ABDBD] hover:text-white shadow-sm cursor-pointer'
                            }`}
                        title={t('voiceRecorder.recordVoiceNote')}
                        data-testid="voice-recorder-start-button"
                    >
                        <Mic size={18} data-testid="voice-recorder-mic-icon" />
                    </MotionButton>
                ) : isRecording ? (
                    <MotionDiv
                        key="recording"
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 'auto', opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        className="flex items-center gap-3 bg-red-50 border border-red-100 px-4 py-2 rounded-2xl"
                        data-testid="voice-recorder-recording-section"
                    >
                        <div className="flex items-center gap-2">
                            <MotionDiv
                                animate={{ opacity: [1, 0, 1] }}
                                transition={{ repeat: Infinity, duration: 1 }}
                                className="w-2.5 h-2.5 bg-red-500 rounded-full"
                                data-testid="voice-recorder-recording-indicator"
                            />
                            <span className="text-red-500 font-mono font-bold text-sm" data-testid="voice-recorder-time-text">{formatTime(recordingTime)}</span>
                        </div>
                        <div className="h-4 w-[1px] bg-red-200" />
                        <button
                            type="button"
                            onClick={stopRecording}
                            className="text-red-500 hover:bg-red-500 hover:text-white p-1 rounded-full transition-all"
                            data-testid="voice-recorder-stop-button"
                        >
                            <Square size={16} fill="currentColor" data-testid="voice-recorder-stop-icon" />
                        </button>
                        <button
                            type="button"
                            onClick={cancelRecording}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            data-testid="voice-recorder-cancel-recording-button"
                        >
                            <Trash2 size={16} data-testid="voice-recorder-trash-icon" />
                        </button>
                    </MotionDiv>
                ) : (
                    <MotionDiv
                        key="preview"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        className="flex items-center gap-1 bg-[#4ABDBD]/5 border border-[#4ABDBD]/15 pl-2 pr-1.5 py-1.5 rounded-2xl flex-shrink-0"
                        data-testid="voice-recorder-preview-section"
                    >
                        <audio src={URL.createObjectURL(audioBlob)} className="h-9 max-w-[200px]" controls data-testid="voice-recorder-preview-audio" />
                        <div className="flex items-center gap-1 ml-1">
                            <button
                                type="button"
                                onClick={sendRecording}
                                disabled={isUploading}
                                className="p-2 bg-[#4ABDBD] text-white rounded-full hover:bg-[#3A9B9B] transition-all shadow-sm"
                                data-testid="voice-recorder-send-button"
                            >
                                {isUploading ? <Loader2 size={16} className="animate-spin" data-testid="voice-recorder-loader-icon" /> : <Send size={16} data-testid="voice-recorder-send-icon" />}
                            </button>
                            <button
                                type="button"
                                onClick={cancelRecording}
                                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                data-testid="voice-recorder-discard-button"
                            >
                                <X size={18} data-testid="voice-recorder-discard-icon" />
                            </button>
                        </div>
                    </MotionDiv>
                )}
            </AnimatePresence>
        </div>
    );
};

export default VoiceRecorder;
